import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { motion, AnimatePresence, useDragControls, useReducedMotion } from "framer-motion";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import {
  FiX, FiTarget, FiLock, FiCheck, FiChevronRight,
  FiShield, FiAward, FiCpu, FiBarChart2, FiLoader,
} from "react-icons/fi";
import { FaSnowflake, FaBullseye } from "react-icons/fa";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { hasRank, LEVEL_DEFINITIONS } from "../../utils/gamificationConfig";
import { pluralizeTasks, pluralizeFreezes } from "../../utils/plural";
import { useDomainSwitch } from "../../utils/domain";
import SessionSetupPopup from "./SessionSetupPopup";
import DomainLevelCard from "./DomainLevelCard";

const apiUrl = process.env.REACT_APP_API_SOURCE;
const goalOptions = [3, 5, 10, 20];

// --- DANE KONFIGURACYJNE ---
const BASIC_STEPS = [
    { id: 'b1', title: "Rozgrzewka", description: "Zalicz pierwsze 20 zadań.", count: 20, type: "total_problems" },
    { id: 'b3', title: "Rozpęd", description: "Dobij do 50 zadań. Łap tempo.", count: 50, type: "total_problems" },
    { id: 'b4', title: "Pierwsza Setka", description: "Przekrocz barierę 100 zadań.", count: 100, type: "total_problems" },
    { id: 'b5', title: "Baza", description: "Wykręć 150 zadań. Buduj nawyk.", count: 150, type: "total_problems" },
    { id: 'b6', title: "Fundamenty", description: "Wbij rangę Pisklak w każdym dziale.", type: "all_fields_rank", rankName: "Pisklak" },
    { id: 'b7', title: "Specjalizacja", description: "Utrzymaj fundamenty + zrób 3 działy na Orzełka.", type: "all_fields_plus_top", baseRank: "Pisklak", topRank: "Orzełek", topCount: 3 },
];

const SOLID_ADDONS = [
    { id: 's1', title: "Poziom Solidny", description: "Podbij wszystkie działy do rangi Orzełek.", type: "all_fields_rank", rankName: "Orzełek" },
    { id: 's2', title: "Elita", description: "Wszystko na Orzełka + wbij 3 Łowców.", type: "all_fields_plus_top", baseRank: "Orzełek", topRank: "Łowca zadań", topCount: 3 },
];

const MASTER_ADDONS = [
    { id: 'm1', title: "God Mode", description: "Zdominuj system. Wszystko na Łowcę.", type: "all_fields_rank", rankName: "Łowca zadań" },
];

// --- ROZSZERZENIE ---
// Zadania otwarte zajmuja kilka razy wiecej czasu niz zamkniete, wiec progi
// liczby zadan sa nizsze. Kamienie rangowe zostaja — progi rang dla dzialow PR
// kalibruje sie w app-config (gamification_config.field_specific).
const PR_BASIC_STEPS = [
    { id: 'p1', title: "Rozgrzewka", description: "Zalicz pierwsze 10 zadań otwartych.", count: 10, type: "total_problems" },
    { id: 'p2', title: "Rozpęd", description: "Dobij do 25 zadań. Łap tempo.", count: 25, type: "total_problems" },
    { id: 'p3', title: "Półsetka", description: "Przekrocz barierę 50 zadań.", count: 50, type: "total_problems" },
    { id: 'p4', title: "Baza", description: "Wykręć 80 zadań. Buduj nawyk.", count: 80, type: "total_problems" },
    { id: 'p5', title: "Fundamenty", description: "Wbij rangę Pisklak w każdym dziale rozszerzenia.", type: "all_fields_rank", rankName: "Pisklak" },
    { id: 'p6', title: "Specjalizacja", description: "Utrzymaj fundamenty + zrób 3 działy na Orzełka.", type: "all_fields_plus_top", baseRank: "Pisklak", topRank: "Orzełek", topCount: 3 },
];

const PR_GOOD_ADDONS = [
    { id: 'pg1', title: "Poziom Solidny", description: "Podbij wszystkie działy rozszerzenia do rangi Orzełek.", type: "all_fields_rank", rankName: "Orzełek" },
    { id: 'pg2', title: "Elita", description: "Wszystko na Orzełka + wbij 3 Łowców.", type: "all_fields_plus_top", baseRank: "Orzełek", topRank: "Łowca zadań", topCount: 3 },
];

const PR_TOP_ADDONS = [
    { id: 'pt1', title: "God Mode", description: "Zdominuj rozszerzenie. Wszystko na Łowcę.", type: "all_fields_rank", rankName: "Łowca zadań" },
];

// Roadmapy sa per poziom: podstawa mierzy sie progiem zdawalnosci,
// rozszerzenie progami rekrutacyjnymi (nie ma progu "zdania").
export const ROADMAP_DATA_PP = {
  PASS: {
    id: "pass",
    label: "Zdać na 30%",
    subLabel: "Cel: Minimum 30%",
    icon: <FiShield />,
    color: "text-emerald-600",
    milestones: [...BASIC_STEPS]
  },
  SOLID: {
    id: "solid",
    label: "Zdać solidnie",
    subLabel: "Cel: Wynik 60%+",
    icon: <FiBarChart2 />,
    color: "text-[#E09F2E]",
    milestones: [...BASIC_STEPS, ...SOLID_ADDONS]
  },
  MAX: {
    id: "max",
    label: "Zdać na maksa",
    subLabel: "Cel: Dominacja 90%+",
    icon: <FiCpu />,
    color: "text-purple-600",
    milestones: [...BASIC_STEPS, ...SOLID_ADDONS, ...MASTER_ADDONS]
  }
};

export const ROADMAP_DATA_PR = {
  COUNTS: {
    id: "counts",
    label: "Żeby się liczyło",
    subLabel: "Cel: 30%+ do rekrutacji",
    icon: <FiShield />,
    color: "text-emerald-600",
    milestones: [...PR_BASIC_STEPS]
  },
  GOOD: {
    id: "good",
    label: "Dobry wynik",
    subLabel: "Cel: Wynik 60%+",
    icon: <FiBarChart2 />,
    color: "text-[#E09F2E]",
    milestones: [...PR_BASIC_STEPS, ...PR_GOOD_ADDONS]
  },
  TOP: {
    id: "top",
    label: "Super wynik",
    subLabel: "Cel: 80%+ ",
    icon: <FiCpu />,
    color: "text-purple-600",
    milestones: [...PR_BASIC_STEPS, ...PR_GOOD_ADDONS, ...PR_TOP_ADDONS]
  }
};

// Domyslne cele, gdy user jeszcze nie wybral.
export const DEFAULT_GOAL_BY_DOMAIN = { PP: "solid", PR: "good" };

export const getRoadmapData = (domain) => (domain === "PR" ? ROADMAP_DATA_PR : ROADMAP_DATA_PP);

// Zachowane dla zgodnosci z importami spoza modala (domyslnie podstawa).
export const ROADMAP_DATA = ROADMAP_DATA_PP;

/**
 * Liczba zadań rozwiązanych W AKTYWNEJ DOMENIE.
 *
 * `problems_solved_total` z profilu jest globalne (podstawa + rozszerzenie), więc
 * kamienie roadmapy PR zaliczałyby się od zadań z podstawy. `available_fields`
 * backend zwraca już przefiltrowane po aktywnej domenie, więc suma po nich
 * daje właściwy licznik dla danego poziomu.
 */
export const sumSolvedInFields = (availableFields = []) =>
    availableFields.reduce((sum, f) => sum + (f.total_solved_per_field || 0), 0);

export const checkMilestoneCompletion = (milestone, totalProblems, availableFields) => {
    if (!milestone) return false;

    if (milestone.type === "total_problems") {
        return totalProblems >= milestone.count;
    }

    if (milestone.type === "all_fields_rank" || milestone.type === "all_fields_plus_top") {
        if (!availableFields || availableFields.length === 0) return false;

        const allBaseMet = availableFields.every(f => hasRank(f, milestone.rankName || milestone.baseRank));

        if (milestone.type === "all_fields_rank") return allBaseMet;

        if (milestone.type === "all_fields_plus_top") {
            if (!allBaseMet) return false;
            const topCountMet = availableFields.filter(f => hasRank(f, milestone.topRank)).length;
            return topCountMet >= milestone.topCount;
        }
    }
    return false;
};

const getRankIconSrc = (rankName) => {
    if (typeof LEVEL_DEFINITIONS !== 'undefined') {
        const rankDef = LEVEL_DEFINITIONS.find(r => r.name === rankName);
        return rankDef ? rankDef.icon.achieved : null;
    }
    return null;
};

// --- KOMPONENT ---
const GoalRoadmapModal = ({
  isOpen, onClose,
  currentGoalId = "solid", currentGoalIdPr = "good", onSaveGoal,
  userStats, availableFields = [],
  dailyGoal = 1, onUpdateDailyGoal,
  streak = 0, freezes = 0,
  activeDomain = "PP",
}) => {
  const [viewingLevelId, setViewingLevelId] = useState(
    activeDomain === "PR" ? currentGoalIdPr : currentGoalId
  );
  const [isGoalExpanded, setIsGoalExpanded] = useState(false);
  const [isSavingGoal, setIsSavingGoal] = useState(false);

  const [selectedDailyGoal, setSelectedDailyGoal] = useState(dailyGoal);
  const [isDailyGoalExpanded, setIsDailyGoalExpanded] = useState(false);

  const [viewingDomain, setViewingDomain] = useState(activeDomain);
  const [isSavingDomain, setIsSavingDomain] = useState(false);
  const { start: startDomainSwitch, overlayProps: domainSwitchOverlay } = useDomainSwitch();

  // TikTok debug
  const [debugModeActive, setDebugModeActive] = useState(false);
  const [showDebugUI, setShowDebugUI] = useState(false);
  const [debugClickCount, setDebugClickCount] = useState(0);
  const [simulatedDate, setSimulatedDate] = useState(new Date());
  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(isOpen);

  const { paddingBottom } = useAndroidSafeArea();

  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('bottomsheet:open'));
      setViewingLevelId(currentGoalId || "solid");
      setIsGoalExpanded(false);
      setSelectedDailyGoal(dailyGoal);
      setIsDailyGoalExpanded(false);
      setViewingDomain(activeDomain || "PP");
      setDebugClickCount(0);
      return () => window.dispatchEvent(new CustomEvent('bottomsheet:close'));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Matura date
  let maturaDate = new Date('2026-05-05');
  try {
    const raw = localStorage.getItem('global_config');
    if (raw) {
      const config = JSON.parse(raw);
      if (config?.content_variables?.matura_timestamp_utc) {
        maturaDate = new Date(config.content_variables.matura_timestamp_utc * 1000);
      }
    }
  } catch (e) {}
  maturaDate.setHours(0, 0, 0, 0);

  const today = debugModeActive ? new Date(simulatedDate) : new Date();
  today.setHours(0, 0, 0, 0);
  const daysToMatura = Math.max(0, Math.ceil((maturaDate - today) / (1000 * 60 * 60 * 24)));
  const tasksToMatura = selectedDailyGoal * daysToMatura;

  const handleSecretTrigger = () => {
    if (debugModeActive) { setShowDebugUI(!showDebugUI); return; }
    const newCount = debugClickCount + 1;
    setDebugClickCount(newCount);
    if (newCount >= 5) { setDebugModeActive(true); setShowDebugUI(true); }
  };

  // Licznik kamieni liczy TYLKO zadania z aktywnego poziomu — inaczej roadmapa PR
  // zaliczalaby sie od zadan z podstawy.
  const totalProblems = sumSolvedInFields(availableFields);
  // Roadmapa i cele sa per poziom — PR ma progi rekrutacyjne zamiast zdawalnosci.
  const roadmapForDomain = getRoadmapData(viewingDomain);
  const fallbackGoal = DEFAULT_GOAL_BY_DOMAIN[viewingDomain] || "solid";
  const safeViewingId = (viewingLevelId || fallbackGoal).toUpperCase();
  const activeRoadmap = roadmapForDomain[safeViewingId]
    || roadmapForDomain[fallbackGoal.toUpperCase()];
  const isCompleted = (m) => checkMilestoneCompletion(m, totalProblems, availableFields);

  const handleGoalSelect = async (levelId) => {
    setViewingLevelId(levelId);
    setIsSavingGoal(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiUrl}update-onboarding-goal/`,
        { goal_id: levelId, domain: viewingDomain },
        { headers: { Authorization: `Token ${token}` } }
      );
      localStorage.setItem('user_current_roadmap_goal', levelId);
      // 2. arg to newDailyGoalCount (kontrakt Dashboardu) — domena idzie jako 3.
      if (onSaveGoal) onSaveGoal(levelId, null, viewingDomain);
    } catch (error) {
      console.error("Błąd zapisu celu:", error);
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleDailyGoalSelect = (goal) => {
    setSelectedDailyGoal(goal);
    onUpdateDailyGoal?.(goal);
  };

  const handleDomainSelect = async (domain) => {
    if (domain === viewingDomain || isSavingDomain) return;
    const previousDomain = viewingDomain;
    setViewingDomain(domain);
    // Kazdy poziom ma wlasny cel — po przelaczeniu pokazujemy cel tego poziomu.
    setViewingLevelId(
      (domain === "PR" ? currentGoalIdPr : currentGoalId)
      || DEFAULT_GOAL_BY_DOMAIN[domain]
    );
    setIsSavingDomain(true);
    try {
      await startDomainSwitch(domain);
    } catch (error) {
      console.error("Błąd zapisu poziomu:", error);
      setViewingDomain(previousDomain);
    } finally {
      setIsSavingDomain(false);
    }
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <SessionSetupPopup key="goal-domain-switch" {...domainSwitchOverlay} />
          <motion.div
            key="goal-backdrop"
            {...getSheetBackdropMotion()}
            className="fixed inset-0 bg-black/50 z-[9990]"
            onClick={onClose}
          />
          <motion.div
            key="goal-sheet"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] text-gray-800 font-sans lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
          >
            <div
              className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* HEADER */}
            <div className="flex items-center justify-between px-5 pt-3 pb-4 bg-white border-b border-gray-100 shrink-0 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
              <h3 className="text-xl font-bold font-apotek tracking-wide text-[#233855] uppercase">
                Moje Cele
              </h3>
              <button
                onClick={onClose}
                className="p-2 bg-gray-50 rounded-full text-gray-400 hover:text-[#233855] transition-colors"
                aria-label="Zamknij"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* SCROLLABLE BODY */}
      <div className="flex-grow overflow-y-auto overscroll-contain bg-white" style={{ paddingBottom: `calc(${paddingBottom} + 2rem)` }}>

        {/* KARTY KONFIGURACJI */}
        <div className="px-4 pt-4 pb-3 space-y-2.5 border-b border-gray-100">

          {/* KARTA: POZIOM NAUKI */}
          <DomainLevelCard
            value={viewingDomain}
            onSelect={handleDomainSelect}
            isSaving={isSavingDomain}
          />

          {/* KARTA: CEL MATURALNY */}
          <div className="group bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.99] transition-all duration-150 overflow-hidden">
            <button
              type="button"
              onClick={() => setIsGoalExpanded(v => !v)}
              className="w-full p-3.5 flex items-center gap-3"
            >
              <div className="flex-shrink-0 p-2 rounded-xl shadow-md bg-[#233855] text-[#F8AB3B] text-xl">
                {activeRoadmap.icon}
              </div>
              <div className="flex-1 text-left">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider leading-none mb-0.5">
                  {activeRoadmap.subLabel}
                </p>
                <p className="text-[15px] font-bold font-apotek text-[#233855] leading-none tracking-wide">
                  {activeRoadmap.label}
                </p>
              </div>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${isGoalExpanded ? 'bg-[#233855]/10' : 'bg-gray-100 group-hover:bg-[#233855]/10'}`}>
                <FiChevronRight className={`w-4 h-4 transition-all duration-200 ${isGoalExpanded ? 'text-[#233855] rotate-90' : 'text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5'}`} />
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isGoalExpanded ? 'max-h-80' : 'max-h-0'}`}>
              <div className="px-4 pb-4">
                <div className="grid grid-cols-3 gap-2.5">
                  {Object.values(roadmapForDomain).map((level) => {
                    const isSelected = viewingLevelId === level.id;
                    return (
                      <button
                        key={level.id}
                        onClick={() => handleGoalSelect(level.id)}
                        className={`flex flex-col items-center p-3 rounded-xl border transition-all active:scale-95 relative overflow-hidden ${
                          isSelected
                            ? "border-[#233855] bg-white shadow-md ring-1 ring-[#233855]/20"
                            : "border-gray-200 bg-gray-50 opacity-70 hover:opacity-100"
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg mb-1.5 shadow-sm ${
                          isSelected ? "bg-[#233855] text-[#F8AB3B]" : "bg-white text-gray-400 border border-gray-100"
                        }`}>
                          {isSavingGoal && isSelected
                            ? <FiLoader className="animate-spin" size={16} />
                            : level.icon
                          }
                        </div>
                        <span className={`text-[10px] font-bold uppercase text-center leading-tight ${isSelected ? "text-[#233855]" : "text-gray-500"}`}>
                          {level.label}
                        </span>
                        {isSelected && <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-[#E09F2E] rounded-full" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* KARTA: CEL DZIENNY */}
          <div className="group bg-white rounded-xl border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.99] transition-all duration-150 overflow-hidden">
            <button
              type="button"
              onClick={() => setIsDailyGoalExpanded(v => !v)}
              className="w-full p-3.5 flex items-center gap-3"
            >
              <div
                onClick={(e) => { e.stopPropagation(); handleSecretTrigger(); }}
                className="flex-shrink-0 p-2 rounded-xl shadow-md bg-[#233855]"
              >
                <FaBullseye className="text-[#F8AB3B]" size={18} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider leading-none mb-0.5">
                  Cel dzienny
                </p>
                <p className="text-[15px] font-bold font-apotek text-[#233855] leading-none tracking-wide">
                  {selectedDailyGoal} {pluralizeTasks(selectedDailyGoal)} / dzień
                </p>
              </div>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${isDailyGoalExpanded ? 'bg-[#233855]/10' : 'bg-gray-100 group-hover:bg-[#233855]/10'}`}>
                <FiChevronRight className={`w-4 h-4 transition-all duration-200 ${isDailyGoalExpanded ? 'text-[#233855] rotate-90' : 'text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5'}`} />
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isDailyGoalExpanded ? 'max-h-[500px]' : 'max-h-0'}`}>
              <div className="px-4 pb-4 space-y-4">

                {showDebugUI && (
                  <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-xs">
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-bold text-red-600">🔧 Panel TikTokera</p>
                      <button onClick={() => setShowDebugUI(false)} className="text-red-500 font-bold px-2 border border-red-200 bg-white rounded">
                        Schowaj
                      </button>
                    </div>
                    <label className="block text-gray-700 font-semibold">Symuluj "dzisiaj":</label>
                    <input
                      type="date"
                      className="mt-1 block w-full border border-gray-300 rounded p-1"
                      onChange={(e) => setSimulatedDate(new Date(e.target.value))}
                      value={simulatedDate.toISOString().split('T')[0]}
                    />
                    <p className="text-gray-500 mt-1">Dni do {maturaDate.toLocaleDateString()}: {daysToMatura}</p>
                  </div>
                )}

                <div className="grid grid-cols-4 gap-2">
                  {goalOptions.map((goal) => (
                    <button
                      key={goal}
                      onClick={() => handleDailyGoalSelect(goal)}
                      className={`py-3 px-1 rounded-xl border-2 transition-all active:scale-95 flex flex-col items-center ${
                        selectedDailyGoal === goal
                          ? 'border-[#233855] bg-[#233855] text-white shadow-md'
                          : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}
                    >
                      <span className="text-lg font-bold font-apotek leading-none">{goal}</span>
                      <span className="text-[10px] font-semibold mt-0.5 leading-none opacity-80">{pluralizeTasks(goal)}</span>
                    </button>
                  ))}
                </div>

                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1.5">Zdążysz zrobić do matury:</p>
                  <div className="relative h-12">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={`${selectedDailyGoal}-${daysToMatura}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute inset-0 flex items-center justify-center text-5xl font-bold font-apotek text-[#233855]"
                      >
                        {tasksToMatura.toLocaleString('pl-PL')}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">zadań do matury</p>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <FaSnowflake className="text-[#233855]/50 flex-shrink-0" size={13} />
                  <p className="text-xs text-gray-400 leading-relaxed">
                    <span className="font-semibold text-gray-500">{freezes} {pluralizeFreezes(freezes)}</span> — automatycznie chronią serię gdy nie masz czasu na naukę. Zdobywasz za 2× i 3× cel dzienny.
                  </p>
                </div>

              </div>
            </div>
          </div>

        </div>

        {/* ROADMAP */}
        <div className="px-6 py-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 whitespace-nowrap">
              Twoja Droga do Sukcesu
            </p>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="relative">
            <div className="absolute left-5 top-1 bottom-12 w-0.5 bg-gray-100 z-0" />

            <div className="space-y-5 relative z-10">
              {activeRoadmap.milestones.map((milestone, index) => {
                const completed = isCompleted(milestone);
                const isNext = !completed && (index === 0 || isCompleted(activeRoadmap.milestones[index - 1]));

                let rankIcon = null;
                if (milestone.type.includes('rank') || milestone.type.includes('plus_top')) {
                  const rName = milestone.rankName || milestone.topRank;
                  rankIcon = getRankIconSrc(rName);
                }

                return (
                  <div key={milestone.id} className="flex items-stretch gap-4">
                    <div className="flex flex-col items-center justify-start pt-1">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center border-[2px] transition-all relative z-10
                        ${completed
                          ? "bg-white border-[#007D00] text-[#007D00]"
                          : isNext
                            ? "bg-white border-[#E09F2E] text-[#E09F2E] scale-110 shadow-[0_0_15px_rgba(224,159,46,0.4)]"
                            : "bg-white border-gray-100 text-gray-300"}
                      `}>
                        {completed
                          ? <FiCheck size={20} className="stroke-[3px]" />
                          : isNext
                            ? <FiTarget size={20} />
                            : <FiLock size={14} />
                        }
                      </div>
                    </div>

                    <div className={`
                      flex-1 p-3.5 rounded-xl flex items-center justify-between transition-all duration-300 border
                      ${completed
                        ? "bg-white border-transparent"
                        : isNext
                          ? "bg-white border-[#233855]/10 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.08)] translate-x-1 scale-[1.02]"
                          : "bg-transparent border-transparent opacity-50"}
                    `}>
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-2 mb-1">
                          {isNext && (
                            <span className="bg-[#233855] text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest shadow-sm">
                              TERAZ
                            </span>
                          )}
                          {completed && (
                            <span className="text-[#007D00] text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                              <FiCheck size={10} /> Zaliczone
                            </span>
                          )}
                        </div>
                        <p className={`text-sm font-bold leading-tight ${completed ? "" : isNext ? "text-[#233855]" : "text-gray-500"}`}>
                          {milestone.title}
                        </p>
                        <p className="text-[11px] font-medium mt-0.5 leading-snug text-gray-400">
                          {milestone.description}
                        </p>
                      </div>

                      <div className="ml-2 shrink-0">
                        {rankIcon ? (
                          <img
                            src={rankIcon}
                            alt="Rank"
                            className={`w-9 h-9 object-contain ${!isNext && !completed ? "opacity-50 grayscale" : ""}`}
                          />
                        ) : milestone.type === 'total_problems' ? (
                          <div className={`flex flex-col items-end ${completed ? "text-[#007D00]" : isNext ? "text-[#233855]" : "text-gray-300"}`}>
                            <span className="text-[8px] font-bold uppercase mr-0.5 opacity-70">cel</span>
                            <span className={`font-apotek font-bold ${isNext ? "text-xl" : "text-lg"}`}>
                              {milestone.count}
                            </span>
                          </div>
                        ) : (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${completed ? "bg-white border border-[#007D00]/30 text-[#007D00]" : "bg-gray-50 text-gray-300"}`}>
                            {completed ? <FiCheck size={16} /> : <FiLock size={14} />}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Final goal card */}
            {(() => {
              const lastMilestone = activeRoadmap.milestones[activeRoadmap.milestones.length - 1];
              const isGoalMet = isCompleted(lastMilestone);

              let finalRankName = "Orzełek";
              if (activeRoadmap.id === 'solid' || activeRoadmap.id === 'max') {
                finalRankName = "Łowca zadań";
              }
              const finalRankIconSrc = getRankIconSrc(finalRankName);

              return (
                <div className={`flex items-stretch gap-4 pt-4 pb-4 relative z-10 transition-all duration-500 ${isGoalMet ? "opacity-100" : "opacity-60 grayscale"}`}>
                  <div className="flex flex-col items-center justify-start relative shrink-0">
                    <div className="w-11 h-11 rounded-xl bg-white z-0 absolute top-0 left-0" />
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-sm border z-10 relative transition-colors duration-300
                      ${isGoalMet ? "bg-[#233855] border-[#233855] text-[#E09F2E]" : "bg-gray-100 border-gray-200 text-gray-400"}`}>
                      {isGoalMet ? <FiAward size={22} /> : <FiLock size={20} />}
                    </div>
                  </div>

                  <div className={`flex-1 rounded-xl p-4 flex flex-col gap-3 shadow-sm border transition-colors duration-300
                    ${isGoalMet ? "bg-white border-[#E09F2E] shadow-[0_4px_15px_-5px_rgba(224,159,46,0.15)]" : "bg-gray-50 border-gray-200"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-[9px] uppercase font-bold text-gray-400 tracking-wider mb-1">Twój cel maturalny</p>
                        <h4 className={`font-bold font-apotek text-xl leading-none tracking-wide transition-colors ${isGoalMet ? "text-[#233855]" : "text-gray-400"}`}>
                          {activeRoadmap.label} {isGoalMet ? "Osiągnięty!" : ""}
                        </h4>
                      </div>
                      {finalRankIconSrc && (
                        <div className={`shrink-0 w-12 h-12 drop-shadow-sm -mt-1 transition-opacity ${isGoalMet ? "opacity-100" : "opacity-40"}`}>
                          <img src={finalRankIconSrc} alt="Rank" className="w-full h-full object-contain" />
                        </div>
                      )}
                    </div>
                    <div className="w-full pt-1 border-t border-gray-100/50">
                      <p className={`text-xs font-medium leading-relaxed transition-colors ${isGoalMet ? "text-gray-500" : "text-gray-400"}`}>
                        {isGoalMet ? (
                          <>Brawo! <span className="text-[#233855] font-bold">Utrzymuj serię i rób powtórki, żeby zawsze być w formie.</span></>
                        ) : (
                          <>To Twój cel ostateczny. <span className="text-gray-500 font-bold">Ukończ wszystkie poprzednie kroki, aby zdobyć to odznaczenie.</span></>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

      </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default GoalRoadmapModal;
